import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Send, Package as PackageIcon, ShoppingBag, DollarSign } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Stat from '@/components/ui/Stat';
import Modal from '@/components/ui/Modal';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import PackageForm from './PackageForm';
import { PACKAGE_STATUS, fmtMoney, fmtNum } from '@/lib/enums';
import styles from './Packages.module.css';

export default function TrainerPackages() {
  const { t } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get('/packages/mine')
      .then((res) => setPackages((res.data || res).packages || res.data || []))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
    api.get('/packages/report').then((res) => setReport(res.data || res)).catch(() => setReport(null));
  };

  useEffect(load, []);

  const save = async (form) => {
    if (editing?.id) await api.put(`/packages/${editing.id}`, form);
    else await api.post('/packages', form);
    setEditing(null);
    load();
  };

  const submit = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/packages/${p.id}/submit`, {});
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader kicker="Movez · Personal" title={t('nav.packages')}>
        <Button icon={Plus} onClick={() => setEditing({})}>{t('screens.newPackage')}</Button>
      </PageHeader>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
        <Stat value={fmtNum(report?.sales ?? 0)} label={t('packages.stats.salesMonth')} tone="hi" icon={<ShoppingBag size={18} strokeWidth={2} />} />
        <Stat value={fmtMoney(report?.gross ?? 0)} label={t('packages.stats.revenue')} tone="accent" icon={<DollarSign size={18} strokeWidth={2} />} />
        <Stat value={`−${fmtMoney(report?.commission ?? 0)}`} label={t('packages.stats.commission')} tone="hi" icon={<PackageIcon size={18} strokeWidth={2} />} />
        <Stat value={fmtMoney(report?.netToTrainer ?? 0)} label={t('packages.stats.youReceive')} tone="recovery" />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><Spinner /></div>
      ) : packages.length === 0 ? (
        <EmptyState icon={PackageIcon} title={t('packages.empty.title')} description={t('packages.empty.description')}
          action={<Button icon={Plus} onClick={() => setEditing({})}>{t('screens.newPackage')}</Button>} />
      ) : (
        <div className={styles.grid}>
          {packages.map((p) => {
            const s = PACKAGE_STATUS[p.status] || { label: p.status, variant: 'neutral' };
            return (
              <Card key={p.id} className={styles.card} padding="none">
                {p.coverUrl && <img src={p.coverUrl} alt="" className={styles.cover} />}
                <div className={styles.body}>
                  <div className={styles.cardHead}>
                    <Badge variant={s.variant}>{s.label}</Badge>
                    <span className={styles.price}>{fmtMoney(p.price)}</span>
                  </div>
                  <div className={styles.title}>{p.title || p.name}</div>
                  {p.description && <p className={styles.desc}>{p.description}</p>}
                  <div className={styles.sales}>{t('packages.salesCount', { count: Number(p.sales ?? p.salesCount ?? 0) })}</div>
                  <div className={styles.actions}>
                    <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setEditing(p)}>{t('common.edit')}</Button>
                    {(p.status === 'draft' || p.status === 'rejected') && (
                      <Button size="sm" variant="outline" icon={Send} loading={busy === p.id} onClick={() => submit(p)}>
                        {t('packages.submitForApproval')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} kicker={editing?.id ? t('packages.modalEditKicker') : t('packages.modalNewKicker')} title={editing?.id ? t('packages.modalEditTitle') : t('screens.newPackage')} size="lg">
        {editing && <PackageForm initial={editing} onSubmit={save} onCancel={() => setEditing(null)} />}
      </Modal>
    </>
  );
}
